/**
 * Shared layout for the Pool module's screens. One "Pool" entry in the main AppShell nav — visible ONLY
 * once GET /pool/access resolves a non-null pool role (see AppShell's `usePoolAccess`-driven filter; a
 * non-operator never sees the item at all) — opens this layout, which renders its own route-driven
 * sub-nav so each screen stays a REAL, bookmarkable URL (/pool/dashboard, /pool/accounts, …) — the same
 * convention CustomerPage uses for its tabs, just backed by router paths instead of a ?tab= param. The
 * screens themselves still assert their own gate independently on every request (see PoolErrorState) —
 * this layout's sub-nav is not itself a security boundary, only a visibility one.
 *
 * rev 6 (POOL-UI-REV6-SPEC.md, R2/R7): tabs are driven entirely by GET /pool/access's `role` +
 * `open_intents`, not by a separate Owner-only check —
 *   - accounts / users — every pool role (owner, admin, viewer).
 *   - dashboard / settings / system / audit / reports — `_manage` only (owner or admin); a viewer
 *     never sees any of these tabs (reports added 30/7 — "Online account history", the daily-snapshot
 *     history behind PoolReportsPage; same gate class as settings/system/audit, not a new one)
 *     (dashboard moved here 30/7 — คุณใหม่: "role Viewer ไม่ให้เห็น dashboard". The server moved with it:
 *     GET /pool/dashboard is `_manage`-gated now, so the bare URL 403s rather than half-rendering, and
 *     /pool with no sub-path lands a viewer on Accounts instead — see PoolIndexRedirect.)
 *     (audit added 21/7 — the module's own audit log, `_manage`-gated the same way as settings/system,
 *     never <PoolErrorState/>'s "not a pool operator" copy — see PoolAuditPage's own banner).
 *   - intents — `_manage` only, AND only when `open_intents > 0` (a red count badge rides on the tab);
 *     it stays reachable at the bare URL with zero open intents (an ordinary empty screen, not a 404),
 *     the tab itself just doesn't advertise it until there's something to look at.
 *   - import — REMOVED (R8): the team keys periods in by hand now, no Excel path left in the FE.
 */

import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router-dom";
import { Badge } from "@/components/ui";
import { usePoolAccess } from "@/features/pool/hooks";
import { cn } from "@/lib/cn";

/** `_manage`-only, but kept FIRST in the strip for those who do see it — an owner/admin's nav order is
 *  unchanged by the 30/7 gate; only a viewer's strip gets shorter (it now opens on Accounts). */
const DASHBOARD_TAB = { to: "/pool/dashboard", key: "dashboard" } as const;

const VIEW_TABS = [
  { to: "/pool/accounts", key: "accounts" },
  { to: "/pool/users", key: "users" },
] as const;

const INTENTS_TAB = { to: "/pool/intents", key: "intents" } as const;

/** `_manage`-only, same gate class as Dashboard/Settings/System/Audit — a pool-Viewer never sees it.
 *  Reports (30/7, "Online account history") is FIRST here so an owner/admin's strip reads Dashboard ·
 *  Accounts · Users · Reports · Settings · System · Audit log — Intents (when it renders at all) still
 *  lands ahead of it, same as it already does ahead of every other MANAGE_TABS entry. */
const MANAGE_TABS = [
  { to: "/pool/reports", key: "reports" },
  { to: "/pool/settings", key: "settings" },
  { to: "/pool/system", key: "system" },
  { to: "/pool/audit", key: "audit" },
] as const;

export function PoolLayout() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();
  const role = accessQ.data?.role ?? null;
  const canManage = role === "owner" || role === "admin";
  const openIntents = accessQ.data?.open_intents ?? 0;
  const showIntents = canManage && openIntents > 0;

  const tabs = [
    ...(canManage ? [DASHBOARD_TAB] : []),
    ...VIEW_TABS,
    ...(showIntents ? [INTENTS_TAB] : []),
    ...(canManage ? MANAGE_TABS : []),
  ];

  return (
    <section className="flex flex-col gap-6">
      <div
        role="tablist"
        aria-label={t("nav.pool")}
        className="flex items-center gap-1 border-b border-(--border)"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            role="tab"
            className={({ isActive }) =>
              cn(
                "relative inline-flex items-center gap-1.5 rounded-t-(--radius-sm) px-3.5 py-2.5 text-(length:--text-sm) font-medium " +
                  "transition-colors duration-(--dur-fast) ease-(--ease-out) " +
                  "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
                isActive
                  ? "text-(--brand-strong) after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded-full after:bg-(--brand)"
                  : "text-(--text-muted) hover:text-(--text)",
              )
            }
          >
            {t(`pool.tabs.${tab.key}`)}
            {tab.key === "intents" && (
              <Badge
                tone="danger"
                aria-label={t("pool.intents.openCountAria", { count: openIntents })}
              >
                {openIntents}
              </Badge>
            )}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </section>
  );
}
